/**
 * New Releases Manager Component
 * Allows admin to curate and reorder the "Newly Released Originals" section
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Plus, X, GripVertical, Trash2, Save, RefreshCw,
  ChevronUp, ChevronDown, Clock, BookOpen, Film, AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { API_URL } from '../config/api';

const API = API_URL;

const NewReleasesManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [curatedItems, setCuratedItems] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState('all');
  const [hasChanges, setHasChanges] = useState(false);

  // Load curated list and settings
  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/site-settings/new-releases`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setEnabled(response.data.enabled || false);
      setCuratedItems(response.data.curated_items || []);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to load new releases settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load candidates for selection
  const loadCandidates = useCallback(async () => {
    setCandidatesLoading(true);
    try {
      const params = { limit: 50 };
      if (searchQuery) params.search = searchQuery;
      if (contentTypeFilter !== 'all') params.content_type = contentTypeFilter;
      
      const response = await axios.get(`${API}/admin/new-releases-candidates`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setCandidates(response.data.candidates || []);
    } catch (error) {
      console.error('Failed to load candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setCandidatesLoading(false);
    }
  }, [searchQuery, contentTypeFilter]);

  useEffect(() => {
    loadSettings();
    loadCandidates();
  }, [loadSettings, loadCandidates]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadCandidates();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, contentTypeFilter, loadCandidates]);

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings/new-releases`, {
        enabled,
        curated_items: curatedItems.map(item => ({
          link_type: item.link_type,
          link_target: item.link_target || item.id,
          pinned: item.pinned || false
        }))
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success('New Releases settings saved!');
      setHasChanges(false);
      loadSettings(); // Reload to get fresh data
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Add item to curated list
  const addItem = (item) => {
    // Check for duplicates
    const exists = curatedItems.some(
      c => c.link_type === item.link_type && (c.link_target === item.link_target || c.id === item.id)
    );
    
    if (exists) {
      toast.error('Item already in curated list');
      return;
    }
    
    if (curatedItems.length >= 50) {
      toast.error('Maximum 50 items allowed');
      return;
    }
    
    setCuratedItems([...curatedItems, { ...item, link_target: item.link_target || item.id }]);
    setHasChanges(true);
    toast.success(`Added "${item.title}" to curated list`);
  };

  // Remove item from curated list
  const removeItem = (index) => {
    const item = curatedItems[index];
    setCuratedItems(curatedItems.filter((_, i) => i !== index));
    setHasChanges(true);
    toast.success(`Removed "${item.title}" from curated list`);
  };

  // Move item up/down
  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= curatedItems.length) return;
    
    const newItems = [...curatedItems];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    setCuratedItems(newItems);
    setHasChanges(true);
  };

  // Toggle enabled
  const handleToggleEnabled = (value) => {
    setEnabled(value);
    setHasChanges(true);
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'novel':
        return <BookOpen className="w-4 h-4 text-purple-400" />;
      case 'series':
      default:
        return <Film className="w-4 h-4 text-blue-400" />;
    }
  };

  // Check if item is already in curated list
  const isInCurated = (item) => {
    return curatedItems.some(
      c => c.link_type === item.link_type && (c.link_target === item.link_target || c.link_target === item.id || c.id === item.id)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-orange-500" />
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Toggle and Save */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={enabled}
              onCheckedChange={handleToggleEnabled}
              className="data-[state=checked]:bg-orange-500"
            />
            <span className="text-sm text-gray-400">
              {enabled ? 'Curated Mode Active' : 'Using Algorithmic Mode'}
            </span>
          </div>
          {hasChanges && (
            <Badge variant="outline" className="border-yellow-500 text-yellow-500">
              Unsaved Changes
            </Badge>
          )}
        </div>
        
        <Button 
          onClick={saveSettings} 
          disabled={saving || !hasChanges}
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Info Banner */}
      {!enabled && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-300">
          <strong>Algorithmic Mode:</strong> The homepage shows the most recently published content automatically.
          Enable curated mode to manually select and order items.
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Candidates */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-400" />
              Recent Releases
            </CardTitle>
            <CardDescription>
              Select content to add to your curated list
            </CardDescription>
            
            {/* Search and Filter */}
            <div className="flex gap-2 mt-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search by title..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-gray-800 border-gray-700"
                />
              </div>
              <Select value={contentTypeFilter} onValueChange={setContentTypeFilter}>
                <SelectTrigger className="w-32 bg-gray-800 border-gray-700">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="series">Comics</SelectItem>
                  <SelectItem value="novel">Novels</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          
          <CardContent className="max-h-[500px] overflow-y-auto space-y-2">
            {candidatesLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-5 h-5 animate-spin text-gray-500" />
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No content found
              </div>
            ) : (
              candidates.map((item) => {
                const inCurated = isInCurated(item);
                return (
                  <div
                    key={`${item.link_type}-${item.id}`}
                    className={`flex items-center gap-3 p-2 rounded-lg border transition-colors ${
                      inCurated 
                        ? 'bg-gray-800/50 border-gray-700 opacity-50' 
                        : 'bg-gray-800/30 border-gray-800 hover:border-orange-500/50'
                    }`}
                  >
                    {/* Thumbnail */}
                    <img
                      src={item.cover_url || 'https://via.placeholder.com/60x80'}
                      alt={item.title}
                      className="w-12 h-16 object-cover rounded"
                    />
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(item.link_type)}
                        <span className="text-sm font-medium text-white truncate">
                          {item.title}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {item.creator_name}
                      </p>
                      <p className="text-xs text-gray-600">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    
                    {/* Add Button */}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => addItem(item)}
                      disabled={inCurated}
                      className={inCurated ? 'text-gray-600' : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'}
                    >
                      {inCurated ? 'Added' : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Right Column: Curated Order */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <GripVertical className="w-5 h-5 text-orange-400" />
              Curated Order
              <Badge variant="outline" className="ml-2">
                {curatedItems.length}/50
              </Badge>
            </CardTitle>
            <CardDescription>
              Drag or use arrows to reorder. Top items appear first on homepage.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="max-h-[500px] overflow-y-auto space-y-2">
            {curatedItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                <p>No items curated yet</p>
                <p className="text-xs mt-1">Add items from the left panel</p>
              </div>
            ) : (
              curatedItems.map((item, index) => (
                <div
                  key={`curated-${item.link_type}-${item.link_target || item.id}`}
                  className="flex items-center gap-2 p-2 rounded-lg bg-gray-800/50 border border-gray-700 group"
                >
                  {/* Rank */}
                  <div className="w-6 h-6 rounded bg-orange-500/20 text-orange-400 text-xs font-bold flex items-center justify-center">
                    {index + 1}
                  </div>
                  
                  {/* Thumbnail */}
                  <img
                    src={item.cover_url || 'https://via.placeholder.com/60x80'}
                    alt={item.title}
                    className="w-10 h-14 object-cover rounded"
                  />
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {getTypeIcon(item.link_type)}
                      <span className="text-sm font-medium text-white truncate">
                        {item.title}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {item.creator_name || 'Unknown'}
                    </p>
                  </div>
                  
                  {/* Reorder Buttons */}
                  <div className="flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => moveItem(index, 'up')}
                      disabled={index === 0}
                      className="h-6 w-6 text-gray-400 hover:text-white"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => moveItem(index, 'down')}
                      disabled={index === curatedItems.length - 1}
                      className="h-6 w-6 text-gray-400 hover:text-white"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Remove Button */}
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => removeItem(index)}
                    className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Preview Info */}
      {enabled && curatedItems.length > 0 && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 text-sm text-green-300">
          <strong>Curated Mode Active:</strong> The homepage will display your {curatedItems.length} curated items in order.
          {curatedItems.length < 12 && (
            <span> Remaining slots will be filled with algorithmic picks.</span>
          )}
        </div>
      )}
    </div>
  );
};

export default NewReleasesManager;
